import { QueryClient, QueryObserver } from "@tanstack/react-query";
import { IQueryClient } from "@workspace/types";
import { injectable } from "inversify";
import { computed, createAtom, IAtom, makeObservable, reaction } from "mobx";

@injectable("Request")
export class StoreQueryClient implements IQueryClient {
  queryClient: QueryClient = new QueryClient();
  queryOptions(): ReturnType<IQueryClient["queryOptions"]> {
    return { queryKey: [] };
  }

  private atom: IAtom = createAtom(
    "StoreQueryClient",
    () => this.startTracking(),
    () => this.stopTracking(),
  );
  private queryObserver = new QueryObserver(
    this.queryClient,
    this.defaultedQueryOptions,
  );

  constructor() {
    makeObservable(this, {
      data: computed,
      result: computed,
    });
  }

  fetch() {
    this.startTracking();
    return this.queryClient.fetchQuery(this.defaultedQueryOptions);
  }

  refetch() {
    this.startTracking();
    return this.queryObserver.refetch();
  }

  invalidateQueries(queryKey: string[]) {
    return this.queryClient.invalidateQueries({ queryKey });
  }

  get result() {
    this.atom.reportObserved();
    this.queryObserver.setOptions(this.defaultedQueryOptions);
    return this.queryObserver.getOptimisticResult(this.defaultedQueryOptions);
  }

  get data(): IQueryClient["data"] {
    const data = this.result.data;
    const result = this.result;

    if (!data && this.defaultedQueryOptions.enabled && !result.isError) {
      throw this.queryObserver.fetchOptimistic(this.defaultedQueryOptions);
    }
    return data;
  }

  private unsubscribe = () => {};

  private startTracking() {
    const unsubscribeReaction = reaction(
      () => this.defaultedQueryOptions,
      () => {
        this.queryObserver.setOptions(this.defaultedQueryOptions);
      },
    );
    const unsubscribeObserver = this.queryObserver.subscribe(() => {
      this.atom.reportChanged();
    });

    this.unsubscribe = () => {
      unsubscribeObserver();
      unsubscribeReaction();
    };
  }
  private stopTracking() {
    this.unsubscribe();
  }
  private get defaultedQueryOptions() {
    return this.queryClient.defaultQueryOptions(this.queryOptions());
  }
}
